/**
 * Independent, in-browser reimplementation of the crash provably-fair algorithm.
 *
 * This file exists so the client NEVER has to take the backend's word for a
 * result. Nothing here calls the API: every value is recomputed locally with the
 * Web Crypto API from the published seed material, then compared against what
 * the backend recorded. A "verified" badge means the browser's own arithmetic
 * agreed — not that a request returned 200.
 *
 * It must stay byte-for-byte identical to the backend:
 *   crash-core-engine/src/modules/provably-fair/multiplayer-outcome.service.js
 *
 * Shared encoding contract (see crash-core-engine/docs/PROVABLY-FAIR.md and the
 * canonical 00-DOCUMENTATION/CRASH_PROVABLY_FAIR_V2_SPEC.md):
 *   • all seeds/hashes are LOWERCASE HEX strings
 *   • every hash input is those strings' UTF-8 bytes — never raw bytes decoded
 *     from hex, which is the classic place two implementations silently diverge
 *   • the round-seed input uses an explicit ':' separator
 *   • the HMAC key is the roundSeed's UTF-8 bytes; the message is the gameSeed's
 *
 * Two algorithm versions live here, and which one runs is read off the round —
 * never guessed from which fields happen to be present:
 *
 *   v1  roundSeed = SHA256(serverSeed + ':' + roundId)
 *   v2  roundSeed = SHA256(serverSeed + ':' + roundId + ':' + c1 + ':' + c2 + ':' + c3)
 *
 * v2 mixes in three client seeds contributed by up to three of the round's own
 * players. Everything after the round seed — the HMAC, the byte extraction, the
 * house-edge formula — is identical in both, which is why only step 2 branches.
 */

// Must equal ROUND_SEED_SEPARATOR in the backend constants. The API also returns
// it per round, and verifyRound() prefers that value over this fallback.
export const ROUND_SEED_SEPARATOR = ':'

const encoder = new TextEncoder()

const toHex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')

const subtle = () => {
  const cryptoObj = globalThis.crypto
  if (!cryptoObj?.subtle) {
    // Web Crypto needs a secure context (https or localhost).
    throw new Error('Web Crypto unavailable — verification requires a secure context (HTTPS)')
  }
  return cryptoObj.subtle
}

/** SHA-256 over the UTF-8 bytes of `text` → lowercase hex. */
export const sha256Hex = async (text) => toHex(await subtle().digest('SHA-256', encoder.encode(text)))

/** HMAC-SHA256(key=keyText, message=messageText) → 32-byte Uint8Array. */
export const hmacSha256Bytes = async (keyText, messageText) => {
  const key = await subtle().importKey(
    'raw',
    encoder.encode(keyText),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )

  const signature = await subtle().sign('HMAC', key, encoder.encode(messageText))
  return new Uint8Array(signature)
}

/** The published commitment for a global server seed: SHA256(serverSeed). */
export const hashServerSeed = (serverSeed) => sha256Hex(serverSeed)

/** roundSeed = SHA256(`${serverSeed}:${roundId}`) — provably fair v1. */
export const deriveRoundSeed = (serverSeed, roundId, separator = ROUND_SEED_SEPARATOR) =>
  sha256Hex(`${serverSeed}${separator}${roundId}`)

// ── Provably fair v2 ────────────────────────────────────────────────────────

export const PROVABLY_FAIR_VERSION = { V1: 1, V2: 2 }

/** Exactly three client seeds per v2 round. */
export const CLIENT_SEED_SLOTS = 3

// Must equal CLIENT_SEED_PATTERN in the backend constants. The alphabet excludes
// ':' — the separator below — so no seed can shift the derivation's fields.
export const CLIENT_SEED_PATTERN = /^[A-Za-z0-9_-]{1,64}$/

export const isValidClientSeed = (seed) =>
  typeof seed === 'string' && CLIENT_SEED_PATTERN.test(seed)

/**
 * The seed strings in SLOT order — the order the backend accepted them, which is
 * the order they were hashed in. Sorting here rather than trusting array order
 * matters: a payload that arrived re-ordered would otherwise hash to a different
 * round seed and report a mismatch that is not really there.
 */
export const toSeedValues = (clientSeeds) =>
  [...(clientSeeds ?? [])]
    .sort((a, b) => Number(a.slot) - Number(b.slot))
    .map((entry) => entry.clientSeed)

/** clientSeedBlock = `${c1}:${c2}:${c3}` */
export const buildClientSeedBlock = (seedValues, separator = ROUND_SEED_SEPARATOR) => {
  if (!Array.isArray(seedValues) || seedValues.length !== CLIENT_SEED_SLOTS) {
    throw new Error(`Provably fair v2 needs exactly ${CLIENT_SEED_SLOTS} client seeds`)
  }

  for (const seed of seedValues) {
    // A missing or malformed seed would silently shift the fields of the
    // derivation input, so this throws rather than coercing.
    if (!isValidClientSeed(seed)) {
      throw new Error('A client seed is missing or not in the permitted alphabet')
    }
  }

  return seedValues.join(separator)
}

/** roundSeed = SHA256(`${serverSeed}:${roundId}:${c1}:${c2}:${c3}`) — v2. */
export const deriveRoundSeedV2 = (
  serverSeed,
  roundId,
  seedValues,
  separator = ROUND_SEED_SEPARATOR
) =>
  sha256Hex(
    `${serverSeed}${separator}${roundId}${separator}${buildClientSeedBlock(seedValues, separator)}`
  )

/**
 * A server fallback for one slot, derived from the round's committed server seed:
 *
 *   fallback(slot) = SHA256(`${serverSeed}:fallback:${roundId}:${slot}`)
 *
 * Must mirror deriveFallbackClientSeed in the backend. Fallbacks are DERIVED, not
 * randomly minted, so they are fixed by the commitment published before betting —
 * and so a player can recompute them here instead of trusting that a value the
 * operator chose after seeing everything else was really random.
 */
export const deriveFallbackClientSeed = (serverSeed, roundId, slot, separator = ROUND_SEED_SEPARATOR) =>
  sha256Hex(`${serverSeed}${separator}fallback${separator}${roundId}${separator}${slot}`)

/**
 * Round seed for whichever version produced the round. The single place that
 * decides, so no caller branches on the version itself.
 *
 * The version comes from the round. Never inferred from whether client seeds are
 * present: a v2 round whose seeds failed to load has to fail verification, not
 * fall through to v1 and render a confident tick for the wrong arithmetic.
 */
export const deriveRoundSeedForVersion = ({
  version,
  serverSeed,
  roundId,
  seedValues,
  separator = ROUND_SEED_SEPARATOR,
}) =>
  Number(version) === PROVABLY_FAIR_VERSION.V2
    ? deriveRoundSeedV2(serverSeed, roundId, seedValues, separator)
    : deriveRoundSeed(serverSeed, roundId, separator)

/** First 4 bytes as a big-endian uint32 — the raw outcome. */
export const bufferToRawOutcome = (bytes) =>
  bytes[0] * 256 ** 3 + bytes[1] * 256 ** 2 + bytes[2] * 256 ** 1 + bytes[3] * 256 ** 0

/**
 * rawOutcome → crash multiplier. Mirrors rawToMultiplier() on the backend,
 * including the 1.00x floor and the operator cap (0 = disabled).
 */
export const rawToMultiplier = ({ rawOutcome, maxRawOutcome, houseEdge, maxMultiplierCap = 0 }) => {
  let multiplier = (maxRawOutcome / (rawOutcome + 1)) * (1 - houseEdge)

  if (multiplier < 1) multiplier = 1
  if (maxMultiplierCap > 0 && maxMultiplierCap < multiplier) multiplier = maxMultiplierCap

  return multiplier
}

/**
 * Backend's normalizeMultiplier(TO_FIXED) — 2dp, standard rounding. The engine
 * stores the unrounded value, so compare at display precision.
 */
export const normalizeMultiplier = (value) =>
  value === null || value === undefined || value === 0 ? 0 : Number(value.toFixed(2))

/**
 * Recompute a finished round end to end and report each step's verdict.
 *
 * Checks, in the order a sceptic would run them:
 *   1. commitment  — SHA256(revealedServerSeed) === the hash published up front
 *   2. roundSeed   — the version's derivation === the recorded round seed. For v1
 *                    that is SHA256(`${serverSeed}:${roundId}`); for v2 it also
 *                    folds in the round's three client seeds, so this step is what
 *                    proves the published seeds are the ones that were used.
 *   3. crashPoint  — HMAC pipeline over the recorded round seed === recorded multiplier
 *
 * Step 1 needs the revealed global seed, which only exists after the epoch
 * rotates; until then it reports `null` (pending) while 2 and 3 still verify.
 * Step 2 likewise needs the revealed seed. Step 3 never does — a player can
 * always confirm the multiplier follows from the round seed the server published.
 *
 * Interrupted rounds are the one nuance: when a restart forces the server to
 * settle a round early, its recorded multiplier is the last one observed rather
 * than the one the seed derives. The API flags that (`interrupted`), and step 3
 * reports its own `interrupted` status instead of `fail` — calling it a failure
 * would accuse an honest operator — while still exposing the derived value so
 * nothing is hidden.
 *
 * @param {object} round - a roundInfo payload
 * @returns {Promise<object>} per-step results + an overall verdict
 */
export const verifyRound = async (round) => {
  const {
    roundId,
    hash: recordedRoundSeed,
    seed: gameSeed,
    serverSeed,
    serverSeedHash,
    crashMultiplier,
    maxRawOutcome,
    houseEdge,
    maxMultiplierCap = 0,
    separator = ROUND_SEED_SEPARATOR,
    interrupted = false,
    // Read off the round, defaulting to v1 for history written before the field
    // existed. Deliberately NOT inferred from whether clientSeeds is populated.
    provablyFairVersion = PROVABLY_FAIR_VERSION.V1,
    clientSeeds = null,
  } = round ?? {}

  const version = Number(provablyFairVersion)
  const isV2 = version === PROVABLY_FAIR_VERSION.V2
  const seedValues = toSeedValues(clientSeeds)

  const result = {
    commitment: { status: 'pending', expected: serverSeedHash ?? null, actual: null },
    roundSeed: { status: 'pending', expected: recordedRoundSeed ?? null, actual: null },
    // v2 only: did every SERVER_FALLBACK slot hold the value the committed server
    // seed derives? `null` for a v1 round, which had no fallbacks.
    fallbacks: isV2 ? { status: 'pending', checked: 0, mismatched: [] } : null,
    crashPoint: { status: 'pending', expected: crashMultiplier ?? null, actual: null },
    rawOutcome: null,
    hex: null,
    version,
    // The seeds this verification actually consumed, so the UI reports what was
    // checked rather than what happened to be on screen.
    clientSeeds: isV2 ? (clientSeeds ?? []) : null,
    verified: false,
    interrupted,
    error: null,
  }

  try {
    if (!recordedRoundSeed || !gameSeed || crashMultiplier == null) {
      result.error = 'Round is not finished yet — no seed published.'
      return result
    }

    // A v2 round MUST publish its three seeds. Missing them is a failed check, not
    // a reason to try the v1 formula: falling back would produce a different round
    // seed, report a mismatch, and blame the operator for the client's own gap.
    if (isV2 && seedValues.length !== CLIENT_SEED_SLOTS) {
      result.error = 'This round used three client seeds, but they were not published with it — cannot verify here.'
      return result
    }

    // 1 + 2 — only possible once this round's server seed has been revealed.
    if (serverSeed) {
      result.commitment.actual = await hashServerSeed(serverSeed)
      result.commitment.status =
        serverSeedHash && result.commitment.actual === serverSeedHash ? 'pass' : 'fail'

      // The version decides the derivation. For v2 the client seeds are inputs, so
      // this step also catches a seed set that was edited after the fact.
      result.roundSeed.actual = await deriveRoundSeedForVersion({
        version,
        serverSeed,
        roundId,
        seedValues,
        separator,
      })
      result.roundSeed.status = result.roundSeed.actual === recordedRoundSeed ? 'pass' : 'fail'

      // 2b — every server-filled slot must hold the DERIVED value. This is what
      // makes a fallback as trustworthy as a player seed: without it an operator
      // could have picked those values after seeing the player seeds and the
      // record would still verify.
      if (isV2) {
        const fallbackEntries = (clientSeeds ?? []).filter(
          (entry) => entry.source === 'SERVER_FALLBACK'
        )

        for (const entry of fallbackEntries) {
          const expected = await deriveFallbackClientSeed(
            serverSeed,
            roundId,
            entry.slot,
            separator
          )
          if (expected !== entry.clientSeed) result.fallbacks.mismatched.push(entry.slot)
        }

        result.fallbacks.checked = fallbackEntries.length
        result.fallbacks.status = result.fallbacks.mismatched.length ? 'fail' : 'pass'
      }
    }

    // 3 — always possible for a finished round.
    const bytes = await hmacSha256Bytes(recordedRoundSeed, gameSeed)
    const rawOutcome = bufferToRawOutcome(bytes)

    const computed = rawToMultiplier({
      rawOutcome,
      maxRawOutcome,
      houseEdge,
      maxMultiplierCap,
    })

    result.hex = toHex(bytes)
    result.rawOutcome = rawOutcome
    result.crashPoint.actual = computed

    // Compare at display precision: the engine derives the same unrounded float
    // but rounds for display, so an exact float compare would fail spuriously.
    const matches = normalizeMultiplier(computed) === normalizeMultiplier(crashMultiplier)

    // A round the server had to settle after a restart records the last
    // multiplier observed, not the one its seed derives. That gap is an
    // interruption, not evidence of tampering, so it gets its own status —
    // reporting 'fail' here would accuse an honest operator.
    result.crashPoint.status = matches ? 'pass' : interrupted ? 'interrupted' : 'fail'

    // Overall: nothing outright failed. For an interrupted round the seed chain
    // still proves the intended result was fixed in advance, which is what the
    // commitment guarantees; the settled figure is reported separately.
    const steps = [
      result.commitment.status,
      result.roundSeed.status,
      result.crashPoint.status,
      result.fallbacks?.status,
    ].filter(Boolean)
    result.verified = !steps.includes('fail')

    return result
  } catch (err) {
    result.error = err?.message ?? 'Verification failed'
    return result
  }
}
